import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, Table, CopyButton } from '../../../components';

export const SecuritySection = ({ id, data }) => {
  const [emailPrivacyExpanded, setEmailPrivacyExpanded] = useState(false);
  const [sslSecureExpanded, setSslSecureExpanded] = useState(false);
  const [mixedContentExpanded, setMixedContentExpanded] = useState(false);

  const defaultCriteria = {
    status: 'info',
    iconType: 'info',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
    isEmpty: true,
    message: 'Data is unavailable.',
    data: null,
  };

  const emailPrivacy = data?.emailPrivacy || defaultCriteria;
  const sslSecure = data?.sslSecure || defaultCriteria;
  const mixedContent = data?.mixedContent || defaultCriteria;

  // Build mixed content table data
  const mixedContentTableData = mixedContent?.data?.httpResources?.length > 0
    ? [['Insecure URL'], ...mixedContent.data.httpResources.map(item => [item.url])]
    : [];

  // Check icon SVG (blue checkmark)
  const CheckIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.3 4L6 11.3L2.7 8" stroke="#207DE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // X icon SVG (red X)
  const XIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4L4 12M4 4L12 12" stroke="#F04D50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <ResultsItem 
      id={id}
      title="Security" 
      tooltip="A secure website is essential. You don't want to shop on or provide personal information to a site that isn't secure — and nobody else does either! It's essential to build a secure site because it'll help your business rank higher in search results and get more value from its traffic."
      errorCount={data?.errorCount || 0}
      toImproveCount={data?.toImproveCount || 0}
    >
      <ResultSubItem
        title="Email Privacy"
        tooltip="Email privacy investigates whether linked or plain-text email addresses exist on your site, which spammers can scrape and use to flood your inbox."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={emailPrivacy?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {emailPrivacy?.isEmpty ? (
              <p className="text-primary">
                {emailPrivacy?.message || 'Good, no email address has been found in plain text.'}
              </p>
            ) : (
              <p className="text-primary">
                Warning! <strong>{emailPrivacy?.data?.emailCount || 0}</strong> email {emailPrivacy?.data?.emailCount === 1 ? 'address has' : 'addresses have'} been found in plain text.
              </p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={emailPrivacyExpanded}
            onClick={() => setEmailPrivacyExpanded(!emailPrivacyExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={emailPrivacyExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Email Privacy Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Avoid adding email addresses to your site, whether as links or plain text</li>
                <li>Replace email addresses on your website with contact forms</li>
                <li>Create a dedicated email address to receive contact form submissions</li>
              </ul>
            </IconContent>
          }
          sideContent={
            emailPrivacy?.importance?.level > 0 || emailPrivacy?.solvability?.level > 0 ? (
              <PriorityIndicatorGroup>
                {emailPrivacy?.importance?.level > 0 && (
                  <PriorityIndicator text={emailPrivacy?.importance?.text || ''} level={emailPrivacy?.importance?.level || 0} />
                )}
                {emailPrivacy?.solvability?.level > 0 && (
                  <PriorityIndicator text={emailPrivacy?.solvability?.text || ''} level={emailPrivacy?.solvability?.level || 0} />
                )}
              </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/general/9-tips-for-designing-contact-forms-that-convert/" target="_blank" rel="noopener noreferrer">
                    9 Tips for Designing Contact Forms That Convert
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/web-design/best-contact-us-pages/" target="_blank" rel="noopener noreferrer">
                    10 Helpful Examples of Contact Pages & Forms
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/lead-generation/learn/how-to-create-effective-lead-generation-forms/" target="_blank" rel="noopener noreferrer">
                    How to Create Effective Lead Generation Forms
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="SSL Secure"
        tooltip="An SSL certificate helps secure your website through HTTPS but requires redirecting your HTTP pages to HTTPS and renewing your certificate each year."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={sslSecure?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {sslSecure?.isEmpty ? (
              <p className="text-primary">
                {sslSecure?.message || 'Warning! No SSL certificate was detected.'}
              </p>
            ) : (
              <>
                <p className="text-primary">
                  {sslSecure?.message || 'Great, your website is SSL secured (HTTPS).'}
                </p>
                <ul className="https-checklist">
                  <li className="https-checklist__item">
                    {sslSecure?.data?.httpsRedirect ? <CheckIcon /> : <XIcon />}
                    <span>
                      {sslSecure?.data?.httpsRedirect 
                        ? "Your website's URLs redirect to HTTPS pages."
                        : "Your website's URLs do not redirect to HTTPS pages."}
                    </span>
                  </li>
                  <li className="https-checklist__item">
                    {sslSecure?.data?.stsHeader ? <CheckIcon /> : <XIcon />}
                    <span>
                      {sslSecure?.data?.stsHeader 
                        ? 'Your headers are properly set up to use HSTS.'
                        : 'Your headers are not properly set up to use HSTS.'}
                    </span>
                  </li>
                  {sslSecure?.data?.certificateExpirationFriendly && (
                    <li className="https-checklist__item">
                      {sslSecure?.data?.certificateExpired ? <XIcon /> : <CheckIcon />}
                      <span>The SSL certificate {sslSecure?.data?.certificateExpirationFriendly}.</span>
                    </li>
                  )}
                  {sslSecure?.data?.certificateIssuer && (
                    <li className="https-checklist__item">
                      <CheckIcon />
                      <span>The certificate issuer is {sslSecure?.data?.certificateIssuer}.</span>
                    </li>
                  )}
                </ul>
              </>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={sslSecureExpanded}
            onClick={() => setSslSecureExpanded(!sslSecureExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={sslSecureExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's SSL Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Set a reminder to renew your SSL certificate each year</li>
                <li>Redirect your HTTP pages to HTTPS after purchasing your SSL certificate</li>
                <li>Update your XML sitemap and robots.txt file after installing your SSL certificate</li>
              </ul>
            </IconContent>
          }
          sideContent={
            sslSecure?.importance?.level > 0 || sslSecure?.solvability?.level > 0 ? (
              <PriorityIndicatorGroup>
                {sslSecure?.importance?.level > 0 && (
                  <PriorityIndicator text={sslSecure?.importance?.text || ''} level={sslSecure?.importance?.level || 0} />
                )}
                {sslSecure?.solvability?.level > 0 && (
                  <PriorityIndicator text={sslSecure?.solvability?.text || ''} level={sslSecure?.solvability?.level || 0} />
                )}
              </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/web-development/learn/does-your-site-need-an-ssl-certificate/" target="_blank" rel="noopener noreferrer">
                    Does Your Site Need an SSL Certificate?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/marketing/site-security-checklist/" target="_blank" rel="noopener noreferrer">
                    Site Security Checklist: How to Secure Your Website
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/web-development/learn/does-my-site-need-https/" target="_blank" rel="noopener noreferrer">
                    Does Your Site Need HTTPS?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Mixed Content"
        tooltip="Mixed content is when your website loads on a secure website (HTTPS), but other elements of your site, like images and videos, load on an insecure HTTP server."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={mixedContent?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {mixedContent?.isEmpty ? (
              <p className="text-primary">
                {mixedContent?.message || 'No mixed content was found.'}
              </p>
            ) : (
              <div className="table-content-wrapper">
                <Table
                  data={mixedContentTableData}
                  className="table-mixed-content"
                  id="mixed-content-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#mixed-content-table" />
                </div>
              </div>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={mixedContentExpanded}
            onClick={() => setMixedContentExpanded(!mixedContentExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={mixedContentExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Mixed Content Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Identify non-HTTPS resources using browser developer tools like Chrome DevTools</li>
                <li>Change HTTP URLs to HTTPS where possible, ensuring all content is served securely</li>
                <li>When appropriate, use protocol-relative URLs (e.g., //example.com/resource.js) to automatically match the page's protocol</li>
              </ul>
            </IconContent>
          }
          sideContent={
            mixedContent?.importance?.level > 0 || mixedContent?.solvability?.level > 0 ? (
              <PriorityIndicatorGroup>
                {mixedContent?.importance?.level > 0 && (
                  <PriorityIndicator text={mixedContent?.importance?.text || ''} level={mixedContent?.importance?.level || 0} />
                )}
                {mixedContent?.solvability?.level > 0 && (
                  <PriorityIndicator text={mixedContent?.solvability?.text || ''} level={mixedContent?.solvability?.level || 0} />
                )}
              </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="https://www.webfx.com/blog/seo/seo-cheat-sheet/" target="_blank" rel="noopener noreferrer">
                    The Technical SEO Cheat Sheet for Webmasters and Developers
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
    </ResultsItem>
  );
};
